import type { LineSeriesOption } from 'echarts/charts'
import type { DataZoomComponentOption, GridComponentOption, LegendComponentOption, ToolboxComponentOption, TooltipComponentOption } from 'echarts/components'
import type { ComposeOption } from 'echarts/core'
import type { SetRequiredDeep } from 'type-fest'
import * as zod from 'zod'
import type { DepartmentPageLine } from '../Device'
import type { DateFormat } from '../PersonalSettings'
import { COMMON_WIDGET_DATA_CONSTRUCTOR, COMMON_WIDGET_ELEMENT_DATA, defaultInterval, IntervalLockSupportSchema, IntervalSupportSchema, MarkableWidgetSchema, MULTIPLE_Y_CHART, TooltipCompareTypeSchema, WidgetElementBufferSupportSchema, WidgetElementSchema, WidgetSchema, type AbstractWidgetData, type BaseWidgetElementDialogProps, type GraphWidgetElementData, type IntervalSupport, type MarkableWidget, type PartialWidget, type PartialWidgetElement, type TooltipCompareType, type WidgetBufferValue, type WidgetConstructor } from './Common'
import type { BaseWidgetDataRequest, WidgetDataResponse } from './Widgets'

export type MultipleYChartOptions = ComposeOption<
  LineSeriesOption
  | TooltipComponentOption
  | GridComponentOption
  | ToolboxComponentOption
  | LegendComponentOption
  | DataZoomComponentOption
>
export type MultipleYChartWidgetDataRequest = BaseWidgetDataRequest & {
  type: 'MULTIPLE_Y_CHART'
  widget: MultipleYChartWidget
  dateFormat: DateFormat
}

export type MultipleYChartWidgetOptionsResponse = WidgetDataResponse & {
  type: 'MULTIPLE_Y_CHART'
  options: MultipleYChartOptions
  compareTypesTooltip: Map<string, TooltipCompareType>
}

export const MultipleYChartWidgetElementSchema = WidgetElementSchema.merge(WidgetElementBufferSupportSchema).extend({
  type: MULTIPLE_Y_CHART,
  color: zod.string().nullable(),
  showAverageValue: zod.boolean(),
  showLowestValue: zod.boolean(),
  showHighestValue: zod.boolean(),
  compareTypeTooltip: TooltipCompareTypeSchema,
  yAxisNumber: zod.number(),
})
export type MultipleYChartWidgetElement = zod.infer<typeof MultipleYChartWidgetElementSchema>
export type MultipleYChartWidgetElementPartial = SetRequiredDeep<PartialWidgetElement<MultipleYChartWidgetElement>, 'type'>

export const MultipleYChartWidgetSchema = WidgetSchema.merge(MarkableWidgetSchema).merge(IntervalSupportSchema).merge(IntervalLockSupportSchema).extend({
  type: MULTIPLE_Y_CHART,
  elements: MultipleYChartWidgetElementSchema.array(),
})
export type MultipleYChartWidget = zod.infer<typeof MultipleYChartWidgetSchema>
export type MultipleYChartWidgetPartial = SetRequiredDeep<PartialWidget<MultipleYChartWidget>, 'type' | 'elements' | `elements.${number}.type`>

export interface MultipleYChartWidgetElementData extends GraphWidgetElementData {
  showAverageValue: boolean
  showLowestValue: boolean
  showHighestValue: boolean
  yAxisNumber: number
  compareTypeTooltip: TooltipCompareType
  values: WidgetBufferValue[]
  compareToValues: WidgetBufferValue[]
  availableUnits: Record<string, string>
}
export interface MultipleYChartWidgetData extends AbstractWidgetData<MultipleYChartWidgetElementData>, MarkableWidget, IntervalSupport {
  type: 'MULTIPLE_Y_CHART'
  elements: MultipleYChartWidgetElementData[]
  timestamps: string[]
  compareToTimestamps: string[]
}
export interface MultipleYChartWidgetElementDialogProps extends BaseWidgetElementDialogProps {
  type: 'MULTIPLE_Y_CHART'
  line?: DepartmentPageLine
  compareEnabled: boolean
  allowBufferCreation: boolean
}

export const DEFAULT_MULTIPLE_Y_CHART_WIDGET_ELEMENT: MultipleYChartWidgetElementPartial = {
  ...COMMON_WIDGET_ELEMENT_DATA,
  pcNumber: null,
  useDefaultPcNumber: true,
  color: null,
  showAverageValue: false,
  showHighestValue: false,
  showLowestValue: false,
  yAxisNumber: 0,
  compareTypeTooltip: 'NONE',
  type: 'MULTIPLE_Y_CHART',
  buffer: null,
}

export const SECOND_DEFAULT_MULTIPLE_Y_CHART_WIDGET_ELEMENT: MultipleYChartWidgetElementPartial = {
  ...DEFAULT_MULTIPLE_Y_CHART_WIDGET_ELEMENT,
  yAxisNumber: 1,
  order: 1,
}

export const MULTIPLE_Y_CHART_WIDGET_CONSTRUCTOR: WidgetConstructor<MultipleYChartWidgetPartial> = () => ({
  type: 'MULTIPLE_Y_CHART',
  ...COMMON_WIDGET_DATA_CONSTRUCTOR(),
  interval: defaultInterval(),
  markAreaElements: [],
  elements: [DEFAULT_MULTIPLE_Y_CHART_WIDGET_ELEMENT, SECOND_DEFAULT_MULTIPLE_Y_CHART_WIDGET_ELEMENT],
})
